import { getConfig } from './utils';

const BuiltUrl = getConfig('REACT_APP_BUILT_API_URL');
const headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json'
};

/** Simple wrapper around the built api response */
export type BuiltResponse = Promise<{} | null>;
/**
 * Exposed service interface for all exported built API methods
 */
export interface BuiltService {
  unexpire(email: string): BuiltResponse;
  register(token: string): BuiltResponse;
  getTosStatus(accessToken: string): BuiltResponse;
  agreeToTos(accessToken: string): BuiltResponse;
  migrate(email: string, password: string): BuiltResponse;
  associateUserAccount(
    idToken: string,
    registrationToken: string
  ): BuiltResponse;
  forgotPassword(email: string): BuiltResponse;
  getBranding(): BuiltResponse;
  getFederatedUser(accessToken: string): BuiltResponse;
  verifyEmailVerificationToken(
    changeId: string,
    changeToken: string
  ): BuiltResponse;
  confirmEmailVerificationToken(
    changeId: string,
    changeToken: string,
    accessToken: string
  ): BuiltResponse;
  getSsoRedirect(email: string): BuiltResponse;
  changeLocale(token: string, email: string, locale: string): BuiltResponse;
}

export function processResponse(response: Response) {
  if (response.status >= 400) {
    return response.json().then(data => {
      // check the JSON response for maintenance mode (a 503)
      // and redirect them to the appropriate route
      if (data && data.maintenanceMode === true) {
        window.location.href = '/maintenance';
        return null;
      }
      throw data;
    });
  }

  if (response.status === 201) {
    return null;
  }

  return response.json();
}

export function unexpire(email: string) {
  return fetch(`${BuiltUrl}/v1.3/federated/unexpire`, {
    headers,
    method: 'POST',
    body: JSON.stringify({ email })
  }).then(processResponse);
}

export function register(token: string) {
  return fetch(`${BuiltUrl}/v1.3/user/register?_registration_token=${token}`, {
    headers,
    // ABE - had to remove this attribute or else the OPTIONS call to
    // /register would succeed, but no additional request to /register
    // would happen, which causes the app to be in a state of confusion
    // credentials: 'include',
    method: 'GET'
  }).then(processResponse);
}

export function getTosStatus(accessToken: string) {
  return fetch(`${BuiltUrl}/v1.3/user/tos`, {
    headers: {
      ...headers,
      authorization: `Bearer ${accessToken}`
    },
    method: 'GET'
  }).then(processResponse);
}

export function agreeToTos(accessToken: string) {
  return fetch(`${BuiltUrl}/v1.3/user/agree_to_tos`, {
    headers: {
      ...headers,
      authorization: `Bearer ${accessToken}`
    },
    method: 'PUT'
  }).then(processResponse);
}

export function migrate(email: string, password: string) {
  return fetch(`${BuiltUrl}/v1.3/federated/migrate`, {
    headers,
    method: 'POST',
    body: JSON.stringify({ email, password })
  }).then(processResponse);
}

export function associateUserAccount(
  idToken: string,
  registrationToken: string
) {
  return fetch(`${BuiltUrl}/v1.3/federated/associate`, {
    headers: {
      ...headers,
      authorization: `Bearer ${idToken}`
    },
    method: 'POST',
    body: JSON.stringify({ registration_token: registrationToken })
  }).then(processResponse);
}

export function forgotPassword(email: string) {
  return fetch(`${BuiltUrl}/v1.3/federated/resetpassword`, {
    headers,
    method: 'POST',
    body: JSON.stringify({
      email
    })
  }).then(processResponse);
}

export function getBranding() {
  return fetch(`${BuiltUrl}/v1.3/branding`, {
    headers
  })
    .then(processResponse)
    .catch(error => null);
}

export function getFederatedUser(accessToken: string) {
  return fetch(`${BuiltUrl}/v1.3/federated/user`, {
    headers: {
      ...headers,
      authorization: `Bearer ${accessToken}`
    },
    method: 'GET'
  }).then(processResponse);
}

/**
 * Checks to see if the provided parameters are valid and receive the email
 * addresses we need to continue with email verification
 *
 * @param changeId
 * @param changeToken
 */
export function verifyEmailVerificationToken(
  changeId: string,
  changeToken: string
) {
  return fetch(
    `${BuiltUrl}/v1.3/email_change/${changeId}?change_token=${changeToken}`,
    {
      headers,
      method: 'GET'
    }
  ).then(processResponse);
}

/**
 * Completes the email verification flow with BAPI
 *
 * @param changeId
 * @param changeToken
 * @param accessToken
 */
export function confirmEmailVerificationToken(
  changeId: string,
  changeToken: string,
  accessToken: string
) {
  return fetch(`${BuiltUrl}/v1.3/email_change/${changeId}/confirm`, {
    headers: {
      ...headers,
      authorization: `Bearer ${accessToken}`
    },
    method: 'PUT',
    body: JSON.stringify({
      changeToken: changeToken
    })
  }).then(processResponse);
}

/**
 * Checks if the provided email address can log in using
 * single sign-on. If allowed, the appropriate redirect
 * URL will be provided.
 *
 * @param email
 */
export function getSsoRedirect(email: string) {
  return fetch(`${BuiltUrl}/v1.3/federated/sso_redirect?email=${email}`, {
    headers,
    method: 'GET'
  }).then(processResponse);
}

export function changeLocale(token: string, email: string, locale: string) {
  return fetch(`${BuiltUrl}/v1.3/user/register/changeLocale`, {
    headers,
    method: 'POST',
    body: JSON.stringify({ email, locale, registration_token: token })
  }).then(processResponse);
}



// WEBPACK FOOTER //
// ./src/built.ts